import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, doc, getDoc, getDocs, query, where, documentId } from 'firebase/firestore';
import { db } from '../firebase/config';
import { StudentUser } from '../types';

export const useUsers = () => {
  const [users, setUsers] = useState<Record<string, StudentUser>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const usersRef = useRef<Record<string, StudentUser>>({});

  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  // Local cache to avoid repeated reads
  const CACHE_KEY = 'users_cache_v1';
  const CACHE_TTL_MS = 60 * 60 * 1000; // 60 minutes

  const readCache = (): Record<string, { data: StudentUser; updatedAt: number }> => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };

  const writeCache = (cacheObj: Record<string, { data: StudentUser; updatedAt: number }>) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObj));
    } catch {}
  };

  // Fetch a single user by UID
  const fetchUser = useCallback(async (uid: string): Promise<StudentUser | null> => {
    try {
      // Cache first
      const cache = readCache();
      const cached = cache[uid];
      if (cached && (Date.now() - cached.updatedAt) < CACHE_TTL_MS) {
        return cached.data;
      }

      // Primary: users collection
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const raw = userDoc.data() as any;
        const normalized: StudentUser = {
          ...(raw as StudentUser),
          uid,
          // Normalize display name fields
          name: raw?.name || raw?.displayName || (raw?.email ? String(raw.email).split('@')[0] : undefined),
          // Preserve avatar if available
          avatar: raw?.avatar || raw?.profilePicture || undefined,
        };
        cache[uid] = { data: normalized, updatedAt: Date.now() };
        writeCache(cache);
        return normalized;
      }

      // Fallback: students collection (legacy)
      const studentDoc = await getDoc(doc(db, 'students', uid));
      if (studentDoc.exists()) {
        const raw = studentDoc.data() as any;
        const normalized: StudentUser = {
          uid,
          email: raw?.email || '',
          name: raw?.name || raw?.displayName || (raw?.email ? String(raw.email).split('@')[0] : undefined),
          role: (raw?.role as any) || 'student',
          status: (raw?.status as any) || 'Offline',
          earnings: Number(raw?.earnings || 0),
          totalrefs: Number(raw?.totalrefs || raw?.totalRefs || 0),
          registern: String(raw?.registern || ''),
          createdAt: (raw?.createdAt as any) || new Date(),
          profileComplete: Boolean(raw?.profileComplete || false),
          avatar: raw?.avatar || raw?.profilePicture || undefined,
          program: (raw?.program as any) || null,
          referralCode: raw?.referralCode || null,
          referredBy: raw?.referredBy || null,
          affiliateLink: raw?.affiliateLink || null,
          underef: raw?.underef || null,
          lastLogin: raw?.lastLogin,
          message_id: raw?.message_id,
          state: raw?.state,
          updatedAt: raw?.updatedAt,
          telephone: raw?.telephone || raw?.phoneNumber || null,
        } as StudentUser;
        cache[uid] = { data: normalized, updatedAt: Date.now() };
        writeCache(cache);
        return normalized;
      }

      return null;
    } catch (err) {
      return null;
    }
  }, []);

  // Fetch multiple users by UIDs
  const fetchUsers = useCallback(async (uids: string[]) => {
    if (uids.length === 0) return;

    console.log('[useUsers] fetchUsers called with uids:', uids);
    setLoading(true);
    setError('');

    try {
      const cache = readCache();
      const now = Date.now();
      const uniqueUids = Array.from(new Set(uids));

      console.log('[useUsers] Processing unique uids:', uniqueUids);

      // Use existing state and cache to skip fetching
      const resultFromCache: Record<string, StudentUser> = {};
      const toFetch: string[] = [];

      uniqueUids.forEach((uid) => {
        if (usersRef.current[uid]) {
          // already in state
          console.log('[useUsers] User already in state:', uid, usersRef.current[uid]);
          resultFromCache[uid] = usersRef.current[uid];
          return;
        }
        const cached = cache[uid];
        if (cached && (now - cached.updatedAt) < CACHE_TTL_MS) {
          console.log('[useUsers] User found in cache:', uid, cached.data);
          resultFromCache[uid] = cached.data;
        } else {
          console.log('[useUsers] User needs to be fetched:', uid);
          toFetch.push(uid);
        }
      });

      // Batch fetch with 'in' queries (max 10 per batch)
      const fetched: Record<string, StudentUser> = {};
      console.log('[useUsers] Users to fetch:', toFetch);
      
      for (let i = 0; i < toFetch.length; i += 10) {
        const batch = toFetch.slice(i, i + 10);
        console.log('[useUsers] Processing batch:', batch);
        
        if (batch.length === 1) {
          console.log('[useUsers] Single user fetch for:', batch[0]);
          const user = await fetchUser(batch[0]);
          if (user) {
            console.log('[useUsers] Single user fetched successfully:', user);
            fetched[batch[0]] = user;
            cache[batch[0]] = { data: user, updatedAt: now };
          } else {
            console.log('[useUsers] Single user fetch failed for:', batch[0]);
          }
          continue;
        }
        
        console.log('[useUsers] Batch query for users collection:', batch);
        const q = query(collection(db, 'users'), where(documentId(), 'in', batch));
        const snap = await getDocs(q);
        const foundIds = new Set<string>();
        
        console.log('[useUsers] Batch query returned', snap.size, 'documents');
        
        snap.forEach((d) => {
          const raw = d.data() as any;
          console.log('[useUsers] Processing user document:', d.id, raw);
          const normalized: StudentUser = {
            ...(raw as StudentUser),
            uid: d.id,
            name: raw?.name || raw?.displayName || (raw?.email ? String(raw.email).split('@')[0] : undefined),
            avatar: raw?.avatar || raw?.profilePicture || undefined,
          };
          console.log('[useUsers] Normalized user:', normalized);
          fetched[d.id] = normalized;
          cache[d.id] = { data: normalized, updatedAt: now };
          foundIds.add(d.id);
        });

        // Fallback fetch for any IDs not returned by users batch (e.g., legacy students collection)
        const missing = batch.filter((id) => !foundIds.has(id));
        console.log('[useUsers] Missing users from batch, trying fallback:', missing);
        
        for (const missingId of missing) {
          console.log('[useUsers] Fallback fetch for:', missingId);
          const user = await fetchUser(missingId);
          if (user) {
            console.log('[useUsers] Fallback fetch successful:', user);
            fetched[missingId] = user;
            cache[missingId] = { data: user, updatedAt: now };
          } else {
            console.log('[useUsers] Fallback fetch failed for:', missingId);
          }
        }
      }

      // Persist cache updates
      writeCache(cache);

      // Merge results
      const merged = { ...resultFromCache, ...fetched };
      console.log('[useUsers] Final merged results:', merged);
      console.log('[useUsers] Result from cache:', resultFromCache);
      console.log('[useUsers] Fetched results:', fetched);
      
      if (Object.keys(merged).length > 0) {
        console.log('[useUsers] Updating users state with:', merged);
        setUsers(prev => {
          const updated = { ...prev, ...merged };
          console.log('[useUsers] New users state:', updated);
          return updated;
        });
      } else {
        console.log('[useUsers] No users to merge, skipping state update');
      }
    } catch (err) {
      console.error('[useUsers] Error in fetchUsers:', err);
      setError('Failed to fetch user information');
    } finally {
      setLoading(false);
    }
  }, [fetchUser]);

  // Get user display name (prioritize username/name over email)
  const getUserDisplayName = (uid: string): string => {
    const user = users[uid];
    
    // Debug logging
    console.log('[useUsers] getUserDisplayName called for uid:', uid, {
      userExists: !!user,
      userName: user?.name,
      userEmail: user?.email
    });
    
    // Priority 1: Use the name field (username)
    if (user?.name && user.name.trim()) {
      return user.name.trim();
    }
    
    // Priority 2: Use email prefix as fallback
    if (user?.email) {
      return user.email.split('@')[0];
    }
    
    return 'Unknown User';
  };

  // Get user avatar
  const getUserAvatar = (uid: string): string | null => {
    const user = users[uid];
    return user?.avatar || null;
  };

  // Get user role
  const getUserRole = (uid: string): string | null => {
    const user = users[uid];
    return user?.role || null;
  };

  return {
    users,
    loading,
    error,
    fetchUsers,
    fetchUser,
    getUserDisplayName,
    getUserAvatar,
    getUserRole
  };
};
